'use client'

import { useCanSendAnalytics } from 'analytics/hooks/useCanSendAnalytics/useCanSendAnalytics'
import { useEECEvents } from 'analytics/hooks/useEECEvents/useEECEvents'
import type { Item } from 'analytics/utils/getEECItems/getEECItems'
import { updateLastViewedProductsCache } from 'cross-selling/utils/getLastViewedCache/getLastViewedCache'
import { usePathname } from 'next/navigation'
import { useEffect, useRef } from 'react'

import { useStoragePreference } from 'cache/hooks/useStoragePreference/useStoragePreference'
import { useColors } from '../../contexts/ColorsProvider/ColorsProvider'
import type { CommonProps } from '../../types/CommonProps'

export type PdpViewItemProps = Pick<CommonProps, 'product' | 'look'> & {
	readonly searchParams: Record<string, string>
}

export function PdpViewItem({
	searchParams,
	product,
	look,
}: PdpViewItemProps): React.ReactNode {
	const pathname = usePathname()

	const { selectedColor } = useColors()

	const { canSendAnalytics } = useCanSendAnalytics()
	const { sendViewItem } = useEECEvents()
	const { storagePreference } = useStoragePreference()

	const firstRender = useRef(true)

	useEffect(() => {
		if (!firstRender.current) {
			firstRender.current = true
		}

		updateLastViewedProductsCache({
			productId: product.id,
			selectedColorId: selectedColor.id,
			selectedLookId: look.id,
			storagePreference,
		})
	}, [pathname, searchParams.c])

	useEffect(() => {
		if (canSendAnalytics && firstRender.current) {
			firstRender.current = false
			const item: Item = {
				productId: product.id,
				colorId: selectedColor.id,
				lookId: look.id,
				lastUnits: selectedColor.lastUnits,
				index: 1,
				quantity: 1,
			}

			sendViewItem({ item })
		}
	}, [canSendAnalytics, pathname, searchParams.c])

	return null
}
